import * as React from 'react'
import { format } from 'date-fns'
import clsx from 'clsx'
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Tooltip,
  Typography
} from '@mui/material'
import { lighten } from '@mui/material/styles'
import makeStyles from '@mui/styles/makeStyles'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { DeleteConfirmDialog } from '../../components/Dialog'
import { Breakdown as BillBreakdown } from '../../Billing/BreakdownDialog'
import { formatNumber } from '../../util'
import { ErrorBoundary } from '../../components/ErrorBoundary'
import { FormDialog as BillForm, FormValues } from '../../Billing/FormDialog'
import { useBills } from '../../Billing/useBills'
import type { Bill } from '../../Billing/types'
import { RateMethod } from '../../types'
import { StructureBilling } from '../types'
import {
  OUTAGE_STATUS_REFRESH_MILLIS,
  OUTAGE_STATUS_SWR_KEY,
  mapOutageStatusToIsElasticsearchOutage
} from '@/Outage/util'
import { defaultOutageStatusResponse, getOutageStatus } from '@/Outage/api'
import useSWR from 'swr'
import { OrderIcon } from '@/components/OrderIcon'
import { generateBill } from '@/Billing/api'
import { SWLoadingButton } from '@/components/LoadingButton'

type SortDirection = 'asc' | 'desc'

type SortOrder = -1 | 0 | 1

type SortableProperty = 'name' | 'start' | 'end' | 'volume' | 'cost'

type RowData = {
  id: string
  name: string
  start: number
  end: number
  volume: number
  cost: number
  rateMethod?: RateMethod
}

type Comparator = (a: RowData, b: RowData, orderBy?: SortableProperty) => SortOrder

// Util ///////////////////////////////////////////////////////////////////////

/**
 * Sort in descending order
 *
 * @param {object} a - First object to compare
 * @param {object} b - Second object to compare
 * @param {string} orderBy - Property name to order by
 *
 * @returns {-1 | 0 | 1} - Sort order
 */
const descendingComparator = (a: RowData, b: RowData, orderBy: SortableProperty) => {
  // $FlowFixMe
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  // $FlowFixMe
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

/**
 * Get sorting function
 *
 * @param {'asc' | 'desc'} order - Direction to sort
 * @param {*} orderBy - Property name to order by
 *
 * @returns {Function} - Function to sort with
 */
const getComparator = (order: SortDirection, orderBy: SortableProperty): Comparator => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy) as SortOrder
    : (a, b) => -descendingComparator(a, b, orderBy) as SortOrder
}

/**
 * Sort row data
 *
 * @param {RowData[]} array - Array of RowData
 * @param {Function} comparator - Comparing/sorting function
 *
 * @returns {RowData[]} - Sorted RowData
 */

const hiddenArrow = () => <span />
const stableSort = (array: RowData[], comparator: Comparator): RowData[] => {
  const stabilizedThis = array.map((el, index) => [el, index])

  stabilizedThis.sort((a, b) => {
    // $FlowFixMe
    const order = comparator(a[0] as RowData, b[0] as RowData)

    if (order !== 0) return order

    return (a as number[])[1] - (b as number[])[1]
  })

  return stabilizedThis.map((el) => el[0] as RowData)
}

/**
 * Convert Bills to table rows
 *
 * @param  {object[]} billArr - Array of Bills
 * @returns {object[]} - Array of rows
 */
const billsToRows = (billArr: Bill[]): RowData[] => {
  return billArr.map((b) => ({
    id: b.id,
    name: b.name || '',
    start: b.startDate,
    end: b.endDate,
    cost: b.cost,
    volume: b.volume,
    rateMethod: b.rateMethod
  }))
}

// Components /////////////////////////////////////////////////////////////////

// Head ///////////////////////////////

const headCells = [
  { id: 'start', numeric: false, disablePadding: false, label: 'Start Date' },
  { id: 'end', numeric: false, disablePadding: false, label: 'End Date' },
  { id: 'volume', numeric: false, disablePadding: false, label: 'Water Usage' },
  { id: 'cost', numeric: false, disablePadding: false, label: 'Cost' },
  { id: 'rateMethod', numeric: false, disablePadding: false, label: 'Rate Method' }
]

type HeadProps = {
  classes: { [x: string]: string }
  order: SortDirection
  orderBy: SortableProperty
  onRequestSort: (event: React.MouseEvent, property: SortableProperty) => void
}

const EnhancedTableHead = ({ classes, order, orderBy, onRequestSort }: HeadProps) => {
  const createSortHandler = (property: SortableProperty) => (event: React.MouseEvent) => {
    onRequestSort(event, property)
  }

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align="center" sortDirection={false}>
            <TableSortLabel IconComponent={hiddenArrow} onClick={createSortHandler(headCell.id as SortableProperty)}>
              {headCell.label}
              {orderBy === headCell.id && (
                <span
                  style={{
                    position: 'absolute',
                    right: '-25px'
                  }}
                >
                  <OrderIcon type={order} />
                </span>
              )}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell align={'center'}>Tenant Costs</TableCell>
        <TableCell align={'justify'}>Edit / Delete</TableCell>
      </TableRow>
    </TableHead>
  )
}

// Toolbar ////////////////////////////

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1)
  },
  highlight:
    theme.palette.mode === 'light'
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85)
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark
        }
}))

const EnhancedTableToolbar = ({ onAdd }) => {
  const classes = useToolbarStyles()

  return (
    <Toolbar className={clsx(classes.root)}>
      <Typography variant="h6" id="tableTitle" component="div">
        Utility Water Bills
      </Typography>
      <SWLoadingButton onClick={onAdd} sx={{ ml: 2 }}>
        Add Bill
      </SWLoadingButton>
    </Toolbar>
  )
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: `${theme.spacing(4)} 0 ${theme.spacing(3)}`
  },
  paper: {
    marginBottom: theme.spacing(2)
  },
  table: {
    minWidth: 650
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1
  }
}))

type Props = {
  bills: Bill[]
  structureId: string
  structureName: string
  isLoading: boolean
  defaultRateMethod: RateMethod
}

export const Billing = ({ bills, structureId, structureName, isLoading, defaultRateMethod }: Props): JSX.Element => {
  const [order, setOrder] = React.useState<SortDirection>('desc')
  const [orderBy, setOrderBy] = React.useState<SortableProperty>('start')
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(5)
  const [isAddBillOpen, setIsAddBillOpen] = React.useState(false)
  const [isEditingBill, setIsEditingBill] = React.useState<string | null>(null)
  const [selectedBillingDetail, setSelectedBillingDetail] = React.useState<StructureBilling | null>(null)
  const [openConfirmModal, setOpenConfirmModal] = React.useState(false)
  const [selectedRowId, setSelectedRowId] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(false)
  const { create, edit, remove } = useBills(structureId)

  const { data: outageStatusResponse = defaultOutageStatusResponse } = useSWR(OUTAGE_STATUS_SWR_KEY, getOutageStatus, {
    refreshInterval: OUTAGE_STATUS_REFRESH_MILLIS
  })

  const generateBillForSelectedId = (selectedBillId) => {
    if (selectedBillId == null) return

    setLoading(true)
    generateBill(selectedBillId, mapOutageStatusToIsElasticsearchOutage(outageStatusResponse))
      .then((resp) => {
        setSelectedBillingDetail(resp)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const classes = useStyles()

  const rows: RowData[] = billsToRows(bills)

  const handleRequestSort = (event: React.MouseEvent, property: SortableProperty) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => setPage(newPage)

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.currentTarget.value, 10))
    setPage(0)
  }

  const handleClickDelete = (rowId) => () => {
    setSelectedRowId(rowId)
    setOpenConfirmModal(true)
  }

  const handleDeleteConfirm = async () => {
    if (selectedRowId) {
      setLoading(true)
      await remove(selectedRowId)
      setLoading(false)
      setSelectedRowId(null)
    }

    setOpenConfirmModal(false)
    setLoading(false)
  }

  const handleCloseDeleteModal = () => {
    setOpenConfirmModal(false)
    setSelectedRowId(null)
  }

  const handleAddNewBill = async (data: FormValues) => {
    const start = (data.startDate ? data.startDate : 0) / 1000
    const end = (data.endDate ? data.endDate : 0) / 1000
    setLoading(true)

    const billRes = await create({
      cost: +data.cost,
      volume: +data.volume,
      start,
      end,
      structureId,
      volume_units: data.unit || 'GALLONS_1K',
      rate_method: data.rateMethod,
      effective_rate: data.effectiveRate
    })

    setIsAddBillOpen(false)
    setLoading(false)
    generateBillForSelectedId(billRes.id)
  }

  const handleEditBill = async (data: FormValues) => {
    const start = (data.startDate ? data.startDate : 0) / 1000
    const end = (data.endDate ? data.endDate : 0) / 1000
    setLoading(true)

    await edit({
      id: isEditingBill,
      cost: Number(data.cost),
      volume: Number(data.volume),
      volume_units: data.unit,
      start,
      end,
      structureId,
      rate_method: data.rateMethod,
      effective_rate: data.effectiveRate
    })
    generateBillForSelectedId(isEditingBill)
    setIsEditingBill(null)
    setLoading(false)
  }

  const emptyRows: number = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage)

  return (
    <ErrorBoundary>
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <EnhancedTableToolbar onAdd={() => setIsAddBillOpen(true)} />
          <TableContainer>
            <Table className={classes.table} size="medium">
              <EnhancedTableHead classes={classes} order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
              <TableBody sx={{ position: 'relative' }}>
                { stableSort(rows, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => {
                      return (
                        <TableRow hover key={row.name}>
                          <TableCell align="center">{format(new Date(row.start * 1000), 'PPP')}</TableCell>
                          <TableCell align="center">{format(new Date(row.end * 1000), 'PPP')}</TableCell>
                          <TableCell align="center">{formatNumber(row.volume, 3)}</TableCell>
                          <TableCell align="center">{`$${formatNumber(row.cost, 2)}`}</TableCell>
                          <TableCell align="center">{row.rateMethod}</TableCell>
                          <TableCell align="center">
                            <SWLoadingButton
                              title={`View ${row.name}`}
                              onClick={() => generateBillForSelectedId(row.id)}
                            >
                              Generate
                            </SWLoadingButton>
                          </TableCell>
                          <TableCell>
                            <div>
                              <Tooltip title={`Edit ${row.name}`}>
                                <IconButton aria-label="edit" onClick={() => setIsEditingBill(row.id)} size="large">
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={`Delete ${row.name}`}>
                                <IconButton aria-label="delete" onClick={handleClickDelete(row.id)} size="large">
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={8} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onRowsPerPageChange={handleChangeRowsPerPage}
            component={'div'}
            onPageChange={handleChangePage}
          />
        </Paper>
      </div>

      <BillForm
        type="ADD"
        open={isAddBillOpen}
        onClose={() => setIsAddBillOpen(false)}
        onSubmit={handleAddNewBill}
        rateMethod={defaultRateMethod}
        loading={loading}
      />

      {/* Edit Bill */}
      <BillForm
        type={'EDIT'}
        open={Boolean(isEditingBill)}
        onClose={() => setIsEditingBill(null)}
        cost={rows.find((r) => r.id === isEditingBill)?.cost}
        volume={rows.find((r) => r.id === isEditingBill)?.volume}
        start={(rows.find((r) => r.id === isEditingBill)?.start || 0) * 1000}
        end={(rows.find((r) => r.id === isEditingBill)?.end || 0) * 1000}
        rateMethod={rows.find((r) => r.id === isEditingBill)?.rateMethod || defaultRateMethod}
        onSubmit={handleEditBill}
        loading={loading}
      />

      {selectedBillingDetail && (
        <BillBreakdown
          open={!!selectedBillingDetail}
          onClose={() => setSelectedBillingDetail(null)}
          structureName={structureName}
          bill={selectedBillingDetail}
        />
      )}

      <DeleteConfirmDialog
        open={openConfirmModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteConfirm}
        title="Do you want to remove this bill?"
        loading={loading}
      />
    </ErrorBoundary>
  )
}
